import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Check } from 'lucide-react';
import './CustomDropdown.css';

const CustomDropdown = ({ 
  options, 
  value, 
  onChange, 
  placeholder = "Select an option...",
  fullWidth = true
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(null);
  const dropdownRef = useRef(null);
  const optionsContainerRef = useRef(null);
  
  const selectedOption = options.find(option => option === value);
  
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Ignore clicks inside the trigger or the portal dropdown options container
      const clickedInsideTrigger = dropdownRef.current && dropdownRef.current.contains(event.target);
      const clickedInsideOptions = optionsContainerRef.current && optionsContainerRef.current.contains(event.target);
      if (clickedInsideTrigger || clickedInsideOptions) return;
      setIsOpen(false);
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  
  useEffect(() => {
    if (isOpen && options.length > 0) {
      // Find index of current value
      const currentIndex = options.findIndex(option => option === value);
      // Do not pre-highlight when there is no current selection to avoid accidental selection
      setHighlightedIndex(currentIndex >= 0 ? currentIndex : null);
    }
  }, [isOpen, options, value]);

  const handleKeyDown = (e) => {
    if (!isOpen) {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
        e.preventDefault();
        e.stopPropagation();
        setIsOpen(true);
      }
      return;
    }

    switch (e.key) {
      case 'Escape':
        setIsOpen(false);
        break;
      case 'Enter':
        // Prevent Enter from triggering the default action (e.g., clicking the primary button)
        e.preventDefault();
        e.stopPropagation();
        if (highlightedIndex !== null) {
          onChange(options[highlightedIndex]);
          setIsOpen(false);
        }
        break;
      case 'ArrowUp':
        e.preventDefault();
        e.stopPropagation();
        setHighlightedIndex(prev => {
          if (prev === null || prev <= 0) return options.length - 1;
          return prev - 1;
        });
        break;
      case 'ArrowDown':
        e.preventDefault();
        e.stopPropagation();
        setHighlightedIndex(prev => {
          if (prev === null || prev >= options.length - 1) return 0;
          return prev + 1;
        });
        break;
      default:
        break;
    }
  };

  // Get position for portal dropdown
  const [dropdownPosition, setDropdownPosition] = useState({
    top: 0,
    left: 0,
    width: 0
  });

  // Update dropdown position when opened
  useEffect(() => {
    if (isOpen && dropdownRef.current) {
      const rect = dropdownRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY + 5, // 5px gap
        left: rect.left + window.scrollX,
        width: rect.width
      });
    }
  }, [isOpen]);

  // Create portal dropdown options
  const renderDropdown = () => {
    if (!isOpen) return null;
    
    return createPortal(
      <div 
        className="dropdown-options-container" 
        ref={optionsContainerRef}
        style={{
          position: 'absolute',
          top: `${dropdownPosition.top}px`,
          left: `${dropdownPosition.left}px`,
          width: `${dropdownPosition.width}px`
        }}
      >
        <ul className="dropdown-options">
          {options.map((option, index) => (
            <li
              key={index}
              className={`dropdown-option ${option === value ? 'selected' : ''} ${highlightedIndex === index ? 'highlighted' : ''}`}
              onClick={() => {
                onChange(option);
                setIsOpen(false);
              }}
              onMouseEnter={() => setHighlightedIndex(index)}
            >
              <span className="option-text">{option}</span>
              {option === value && (
                <Check className="check-icon" size={16} />
              )}
            </li>
          ))}
        </ul>
      </div>,
      document.body
    );
  };

  return (
    <div 
      className={`custom-dropdown ${fullWidth ? 'full-width' : ''}`} 
      ref={dropdownRef}
      tabIndex="0"
      onKeyDown={handleKeyDown}
      aria-expanded={isOpen}
      role="combobox"
    >
      <div 
        className={`dropdown-selected ${!selectedOption ? 'placeholder' : ''} ${isOpen ? 'open' : ''}`}
        onClick={() => setIsOpen(prev => !prev)}
      >
        <span>{selectedOption || placeholder}</span>
        <ChevronDown className={`dropdown-icon ${isOpen ? 'open' : ''}`} size={20} />
      </div>
      
      {renderDropdown()}
    </div>
  );
};

export default CustomDropdown;
